import { Signal, computed, signal } from '@angular/core';

/** A per-user preference: what the account chose, and how to change it. */
export interface UserPreference<T> {
  /** The account's value, or `empty` while no account is known. */
  readonly value: Signal<T>;
  /** Records and persists a new value. A write with no account behind it is dropped. */
  set(value: T): void;
}

export interface UserPreferenceOptions<T> {
  /** The signed-in user's id, `undefined` for the whole boot window and after logout. */
  userId: Signal<string | undefined>;
  read(userId: string): T;
  write(userId: string, value: T): void;
  /** What the preference reads as before any account is known. Keep it a stable instance. */
  empty: T;
}

/**
 * A stored preference that belongs to one account, read lazily so that nothing is read before
 * the user id exists — the key cannot be built without it, and identity is asynchronous now.
 *
 * The value set during this tab's life is remembered *alongside the id it was set under*, which
 * is what closes the bug scoping exists to close: log out, log in as someone else on the same
 * browser, and the previous account's choice is discarded rather than inherited. A key scoped by
 * user is not enough on its own — a value already sitting in memory outlives its key.
 */
export function userPreference<T>({ userId, read, write, empty }: UserPreferenceOptions<T>): UserPreference<T> {
  const chosen = signal<{ userId: string; value: T } | null>(null);

  return {
    value: computed(() => {
      const id = userId();
      if (!id) return empty;
      const current = chosen();
      return current?.userId === id ? current.value : read(id);
    }),
    set(value: T): void {
      const id = userId();
      if (!id) return;
      chosen.set({ userId: id, value });
      write(id, value);
    },
  };
}
